/**
 * Iter 76 — Form Field Configuration admin page.
 *
 * Super_admin only. Picks a form from the sidebar, then flips toggles
 * per field: required / optional / hidden, with optional label & help-text
 * overrides.
 *
 * Save = bulk PUT /form-config/{form_id}.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Layout, Save, RotateCcw, Eye, EyeOff, AlertTriangle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '../../lib/api';
import { clearFieldConfigCache } from '../../hooks/useFieldConfig';

export default function FormFieldConfig() {
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [editing, setEditing] = useState(null); // current form's merged config
  const [overrides, setOverrides] = useState({}); // {field_id: {is_required, is_hidden, label_override, help_text}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/form-config/forms');
      setForms(r.forms || []);
      if (r.forms?.length && !selectedFormId) {
        setSelectedFormId(r.forms[0].form_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [selectedFormId]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selectedFormId) return;
    (async () => {
      try {
        const r = await apiGet(`/form-config/${selectedFormId}`);
        setEditing(r);
        // Seed the overrides state from current values
        const o = {};
        for (const f of r.fields) {
          o[f.id] = {
            is_required: f.is_required,
            is_hidden: f.is_hidden,
            label_override: f.has_override ? f.label : '',
            help_text: f.help_text || '',
          };
        }
        setOverrides(o);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load form config');
      }
    })();
  }, [selectedFormId]);

  const updateOverride = (fieldId, patch) => {
    setOverrides(prev => ({ ...prev, [fieldId]: { ...prev[fieldId], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const overridesList = Object.entries(overrides).map(([field_id, v]) => ({
        field_id,
        is_required: v.is_required,
        is_hidden: v.is_hidden,
        label_override: v.label_override || null,
        help_text: v.help_text || null,
      }));
      await apiPut(`/form-config/${selectedFormId}`, { overrides: overridesList });
      clearFieldConfigCache(selectedFormId);
      toast.success(`Saved ${editing.label}`);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetField = (fieldId) => {
    const orig = editing.fields.find(f => f.id === fieldId);
    if (!orig) return;
    updateOverride(fieldId, {
      is_required: orig.default_required,
      is_hidden: false,
      label_override: '',
      help_text: '',
    });
  };

  const filteredForms = forms.filter(f =>
    !search || f.label.toLowerCase().includes(search.toLowerCase()) || f.form_id.includes(search.toLowerCase())
  );

  return (
    <div className="bg-[#F8F5EE] min-h-screen" data-testid="form-field-config">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <Link to="/app/settings" className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back to Settings
          </Link>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Layout size={20} className="text-[#1F7A6E]" /> Form Field Configuration
          </h1>
          <p className="text-xs text-slate-500 mt-1">Mark any field across the platform as required, optional, or hidden. Override labels and add help text. Super Admin only.</p>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {/* Sidebar — form picker */}
            <aside className="col-span-3 bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                <Search size={12} className="text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search forms…"
                  className="flex-1 bg-transparent text-xs focus:outline-none"
                  data-testid="form-search"
                />
              </div>
              <ul className="max-h-[600px] overflow-y-auto">
                {filteredForms.map(f => (
                  <li key={f.form_id}>
                    <button onClick={() => setSelectedFormId(f.form_id)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 hover:bg-slate-50 ${selectedFormId === f.form_id ? 'bg-teal-50 border-l-4 border-l-[#1F7A6E]' : ''}`}
                      data-testid={`form-pick-${f.form_id}`}
                    >
                      <div className="font-display font-bold">{f.label}</div>
                      <div className="text-[10px] text-slate-500">
                        {f.module} · {f.field_count} fields
                        {f.override_count > 0 && <span className="ml-1 text-amber-600">· {f.override_count} overridden</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main editor */}
            <main className="col-span-9 bg-white border border-slate-200 rounded-sm">
              {!editing ? (
                <div className="p-8 text-center text-sm text-slate-500">Select a form to configure.</div>
              ) : (
                <>
                  <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <h2 className="font-display font-bold text-lg">{editing.label}</h2>
                      <div className="text-[11px] text-slate-500">{editing.fields.length} fields · {editing.module}</div>
                    </div>
                    <button onClick={save} disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-display font-bold uppercase tracking-wider bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] disabled:opacity-50"
                      data-testid="save-form-config"
                    >
                      {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="text-left px-4 py-2 font-display font-bold">Field</th>
                          <th className="text-left px-4 py-2 font-display font-bold">Group</th>
                          <th className="text-center px-4 py-2 font-display font-bold">Required</th>
                          <th className="text-center px-4 py-2 font-display font-bold">Hidden</th>
                          <th className="text-left px-4 py-2 font-display font-bold">Label Override</th>
                          <th className="text-left px-4 py-2 font-display font-bold">Help Text</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {editing.fields.map(f => {
                          const ov = overrides[f.id] || {};
                          const changed = (ov.is_required !== f.default_required)
                            || ov.is_hidden
                            || (ov.label_override && ov.label_override !== f.label)
                            || ov.help_text;
                          return (
                            <tr key={f.id} className={changed ? 'bg-amber-50/30' : ''} data-testid={`field-row-${f.id}`}>
                              <td className="px-4 py-2 font-mono">
                                <div>{f.id}</div>
                                <div className="text-[10px] text-slate-500 font-sans">{f.type}</div>
                              </td>
                              <td className="px-4 py-2 text-slate-600">{f.group}</td>
                              <td className="px-4 py-2 text-center">
                                <input type="checkbox" checked={!!ov.is_required}
                                  onChange={e => updateOverride(f.id, { is_required: e.target.checked })}
                                  className="w-3.5 h-3.5"
                                  data-testid={`req-${f.id}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-center">
                                <input type="checkbox" checked={!!ov.is_hidden}
                                  onChange={e => updateOverride(f.id, { is_hidden: e.target.checked })}
                                  className="w-3.5 h-3.5"
                                  data-testid={`hide-${f.id}`}
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input type="text" value={ov.label_override || ''}
                                  onChange={e => updateOverride(f.id, { label_override: e.target.value })}
                                  placeholder={f.label}
                                  className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input type="text" value={ov.help_text || ''}
                                  onChange={e => updateOverride(f.id, { help_text: e.target.value })}
                                  placeholder="Add inline help…"
                                  className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
                                />
                              </td>
                              <td className="px-4 py-2 text-right">
                                {changed && (
                                  <button onClick={() => resetField(f.id)} title="Reset to default"
                                    className="text-slate-400 hover:text-rose-600">
                                    <RotateCcw size={11} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-slate-200 text-[10px] text-slate-500 flex items-start gap-2">
                    <AlertTriangle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      Changes apply to NEW form submissions immediately for any form already wired into the framework
                      (Employee Master, Project Create, Lead Create as of Iter 76). Forms not yet wired will treat the
                      saved config as informational until refactored — the helper is `useFieldConfig(formId)`.
                    </div>
                  </div>
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
